import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ColorModeProvider, useColorMode } from './contexts/ColorModeContext';
import { BatchSelectionProvider } from './contexts/BatchSelectionContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { DashboardPage } from './pages/Dashboard/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import InvoiceFormPage from './pages/InvoiceFormPage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import EstimatesPage from './pages/EstimatesPage';
import EstimateDetailPage from './pages/EstimateDetailPage';
import EstimateFormPage from './pages/EstimateFormPage';
import ItemsPage from './pages/ItemsPage';
import ExpensesPage from './pages/ExpensesPage';
// import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import { BusinessSetupModal } from './components/BusinessSetupModal';
import { useBusinessSetupValidation } from './hooks/useBusinessSetupValidation';
import MassInvoiceWizard from './pages/Invoices/MassInvoiceWizard';
import HomePage from './pages/marketing/HomePage';
import FeaturesPage from './pages/marketing/FeaturesPage';
import PricingPage from './pages/marketing/PricingPage';
import PortalApp from './custportal/PortalApp';
import { ReleaseAnnouncementMount } from './components/ReleaseAnnouncementMount';
import PortalAdminPage from './pages/PortalAdminPage';

import CheckEmailPage from './pages/CheckEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { FeedbackList } from './pages/admin/FeedbackList';
import VerifyPage from './pages/VerifyPage';
import FirstRunWizard from './pages/FirstRunWizard';

// Bridge MUI theme to ColorModeContext
const MuiThemeAdapter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useColorMode();
  const theme = React.useMemo(() => createTheme({ palette: { mode } }), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Main App Content
const AppContent: React.FC = () => {
  const { user, currentBusiness, businesses, setCurrentBusiness, isLoading } = useAuth();
  const { mode, setMode } = useColorMode();
  const { isValid, missingCriticalFields, missingWarningFields, isLoading: validationLoading } = useBusinessSetupValidation();
  const [showSetupModal, setShowSetupModal] = useState(false);

  console.log('[AppContent] Render state:', { 
    user: user?.email, 
    currentBusiness: currentBusiness?.name, 
    isLoading,
    businessesCount: businesses.length,
    isValid,
    missingCriticalFields,
    missingWarningFields
  });

  // Add detailed debugging for business setup validation
  console.log('[AppContent] Business Setup Debug:', {
    currentBusiness: currentBusiness ? {
      name: currentBusiness.name,
      street: currentBusiness.street,
      city: currentBusiness.city,
      state: currentBusiness.state,
      zip: currentBusiness.zip,
      phone: currentBusiness.phone,
      email: currentBusiness.email,
    } : null,
    validationState: {
      isValid,
      missingCriticalFields,
      missingWarningFields,
      isLoading: validationLoading
    },
    modalTriggerConditions: {
      hasCurrentBusiness: !!currentBusiness,
      isNotValid: !isValid,
      hasMissingFields: missingCriticalFields.length > 0,
      notLoading: !isLoading && !validationLoading,
      shouldShowModal: !isLoading && !validationLoading && currentBusiness && !isValid && missingCriticalFields.length > 0
    }
  });

  // Show setup modal when validation fails
  useEffect(() => {
    if (!isLoading && !validationLoading && currentBusiness && !isValid && missingCriticalFields.length > 0) {
      setShowSetupModal(true);
    }
  }, [isLoading, validationLoading, currentBusiness, isValid, missingCriticalFields]);

  const handleDarkModeToggle = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  const handleSetupComplete = () => {
    setShowSetupModal(false);
  };

  if (isLoading || validationLoading) {
    console.log('[AppContent] Loading state');
    return <div>Loading...</div>;
  }

  if (!user) {
    console.log('[AppContent] No user, showing login routes');
    return (
      <Routes>
        <Route path="/custportal/*" element={<PortalApp />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // Gate first-time login to the wizard route only
  if (user.first_time_login) {
    return (
      <Routes>
        <Route path="/first-run" element={<FirstRunWizard />} />
        <Route path="*" element={<Navigate to="/first-run" replace />} />
      </Routes>
    );
  }

  // Show setup modal if business setup is incomplete
  if (currentBusiness && !isValid && missingCriticalFields.length > 0) {
    return (
      <BusinessSetupModal 
        open={showSetupModal} 
        onComplete={handleSetupComplete}
      />
    );
  }

  console.log('[AppContent] User authenticated, showing main app routes');
  return (
    <>
      <Layout
        currentBusiness={currentBusiness}
        onBusinessChange={setCurrentBusiness}
        businesses={businesses}
        darkMode={mode === 'dark'}
        onDarkModeToggle={handleDarkModeToggle}
      >
        <Routes>
          <Route path="/custportal/*" element={<PortalApp />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/new" element={<InvoiceFormPage />} />
          <Route path="/invoices/mass-create" element={<MassInvoiceWizard />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/invoices/:id/edit" element={<InvoiceEditPage />} />
          <Route path="/estimates" element={<EstimatesPage />} />
          <Route path="/estimates/new" element={<EstimateFormPage />} />
          <Route path="/estimates/:id" element={<EstimateDetailPage />} />
          <Route path="/estimates/:id/edit" element={<EstimateFormPage />} />
          <Route path="/items" element={<ItemsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          {/* <Route path="/reports" element={<ReportsPage />} /> */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin/feedback" element={<FeedbackList />} />
          <Route path="/portal-admin" element={<PortalAdminPage />} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Layout>

      {missingWarningFields.includes('email') && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 4,
          padding: '8px 12px',
          fontSize: '14px',
          color: '#856404',
          zIndex: 1000,
        }}>
          ⚠️ Email address is recommended for your business profile
        </div>
      )}

      {/* Release announcement modal (business app only) */}
      <ReleaseAnnouncementMount />
    </>
  );
};

// Main App Component
function App() {
  return (
    <ColorModeProvider>
      <MuiThemeAdapter>
        <AuthProvider>
          <BatchSelectionProvider>
            <AppContent />
          </BatchSelectionProvider>
        </AuthProvider>
      </MuiThemeAdapter>
    </ColorModeProvider>
  );
}

export default App;
